import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Loader2, Power, Trash2, MessagesSquare, Search, CircleCheck } from 'lucide-react';
import { apiFetch, apiFetchPaginated } from '../../lib/api';
import { notify } from '../../lib/notify';
import { whatsappGroupSchema, type WhatsappGroupInput } from '../../lib/schemas';
import { Modal } from '../../components/Modal';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { useAuthStore } from '../../lib/auth-store';

interface WhatsappGroup {
  id: number;
  group_jid: string;
  name: string;
  is_active: boolean;
  created_by: number | null;
  created_at: string;
  updated_at: string;
}

export default function WhatsappGroups() {
  const role = useAuthStore((s) => s.user?.role);
  const canEdit = role === 'admin' || role === 'supervisor';
  const canDelete = role === 'admin';

  const [groups, setGroups] = useState<WhatsappGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadTick, setReloadTick] = useState(0);

  const [createOpen, setCreateOpen] = useState(false);
  const [toggleTarget, setToggleTarget] = useState<WhatsappGroup | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<WhatsappGroup | null>(null);

  useEffect(() => {
    setLoading(true);
    setError('');
    apiFetchPaginated<WhatsappGroup>('/admin/whatsapp/groups?per_page=100')
      .then((res) => setGroups(res.data))
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false));
  }, [reloadTick]);

  const refresh = () => setReloadTick((t) => t + 1);

  const doToggle = async () => {
    if (!toggleTarget) return;
    try {
      await apiFetch(`/admin/whatsapp/groups/${toggleTarget.id}`, {
        method: 'PATCH',
        body: { is_active: !toggleTarget.is_active },
      });
      notify.success(
        toggleTarget.is_active ? 'Grupo desactivado.' : 'Grupo reactivado.',
        toggleTarget.name,
      );
      refresh();
    } catch (err) {
      notify.apiError(err, 'No se pudo cambiar el estado.');
      throw err;
    }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    try {
      await apiFetch(`/admin/whatsapp/groups/${deleteTarget.id}`, { method: 'DELETE' });
      notify.success('Grupo eliminado.', deleteTarget.name);
      refresh();
    } catch (err) {
      notify.apiError(err, 'No se pudo eliminar.');
      throw err;
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-slate-500">
            Solo los grupos registrados acá pueden generar tickets cuando el bot es etiquetado.
          </p>
        </div>
        {canEdit && (
          <button
            onClick={() => setCreateOpen(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 text-white text-sm rounded-lg hover:bg-slate-800 transition"
          >
            <Plus size={16} /> Agregar grupo
          </button>
        )}
      </div>

      {canEdit && (
        <DetectGroupsPanel registeredJids={groups.map((g) => g.group_jid)} onRegistered={refresh} />
      )}

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 flex items-center justify-center text-slate-500">
            <Loader2 className="animate-spin" size={20} />
          </div>
        ) : error ? (
          <div className="p-6 text-sm text-rose-600">{error}</div>
        ) : groups.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">
            <MessagesSquare className="mx-auto mb-2 text-slate-300" size={32} />
            Aún no hay grupos registrados.
            {canEdit && ' Agrega el primero para empezar a recibir tickets desde WhatsApp.'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-4 py-2.5 font-medium">Nombre</th>
                  <th className="text-left px-4 py-2.5 font-medium">JID</th>
                  <th className="text-left px-4 py-2.5 font-medium">Estado</th>
                  <th className="text-left px-4 py-2.5 font-medium">Creado</th>
                  {canEdit && <th className="text-right px-4 py-2.5 font-medium">Acciones</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {groups.map((g) => (
                  <tr key={g.id} className="hover:bg-slate-50">
                    <td className="px-4 py-2.5 text-slate-900 font-medium">{g.name}</td>
                    <td className="px-4 py-2.5 text-slate-600 font-mono text-xs">{g.group_jid}</td>
                    <td className="px-4 py-2.5">
                      <span
                        className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                          g.is_active
                            ? 'text-emerald-700 bg-emerald-50'
                            : 'text-slate-500 bg-slate-100'
                        }`}
                      >
                        {g.is_active ? 'Activo' : 'Inactivo'}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-slate-500 text-xs">
                      {new Date(g.created_at).toLocaleDateString('es-VE')}
                    </td>
                    {canEdit && (
                      <td className="px-4 py-2.5 text-right">
                        <div className="inline-flex items-center gap-0.5">
                          <button
                            onClick={() => setToggleTarget(g)}
                            title={g.is_active ? 'Desactivar' : 'Reactivar'}
                            className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                          >
                            <Power size={14} />
                          </button>
                          {canDelete && (
                            <button
                              onClick={() => setDeleteTarget(g)}
                              title="Eliminar"
                              className="p-1.5 rounded-md text-slate-500 hover:bg-rose-50 hover:text-rose-700"
                            >
                              <Trash2 size={14} />
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {canEdit && (
        <CreateGroupModal
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreated={(name) => {
            setCreateOpen(false);
            notify.success('Grupo registrado.', name);
            refresh();
          }}
        />
      )}

      <ConfirmDialog
        open={toggleTarget !== null}
        onOpenChange={(o) => !o && setToggleTarget(null)}
        title={toggleTarget?.is_active ? 'Desactivar grupo' : 'Reactivar grupo'}
        description={
          toggleTarget && (
            <>
              {toggleTarget.is_active
                ? 'Las menciones al bot en este grupo dejarán de generar tickets. Los tickets ya creados se mantienen.'
                : 'Las menciones al bot volverán a generar tickets como antes.'}
              <br />
              <strong className="text-slate-900">{toggleTarget.name}</strong>
            </>
          )
        }
        confirmLabel={toggleTarget?.is_active ? 'Desactivar' : 'Reactivar'}
        tone={toggleTarget?.is_active ? 'warning' : 'default'}
        onConfirm={doToggle}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
        title="Eliminar grupo"
        description={
          deleteTarget && (
            <>
              Se eliminará el mapping de <strong>{deleteTarget.name}</strong>. Los tickets ya
              creados desde este grupo se mantienen, pero futuras menciones se ignorarán.
            </>
          )
        }
        confirmLabel="Eliminar"
        tone="danger"
        onConfirm={doDelete}
      />
    </div>
  );
}

function CreateGroupModal({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (name: string) => void;
}) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<WhatsappGroupInput>({
    resolver: zodResolver(whatsappGroupSchema),
    defaultValues: { is_active: true },
  });

  useEffect(() => {
    if (!open) reset({ is_active: true });
  }, [open, reset]);

  const onSubmit = async (data: WhatsappGroupInput) => {
    try {
      await apiFetch('/admin/whatsapp/groups', { method: 'POST', body: data });
      onCreated(data.name);
    } catch (err) {
      notify.apiError(err, 'No se pudo registrar el grupo.');
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Registrar grupo de WhatsApp">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Nombre del grupo
          </label>
          <input
            type="text"
            placeholder="Ej: Hotel VIP La Guaira — Recepción"
            {...register('name')}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
          />
          {errors.name && (
            <p className="mt-1 text-xs text-rose-600">{errors.name.message}</p>
          )}
          <p className="mt-1 text-xs text-slate-500">
            Es el nombre que vas a ver en el listado de tickets como origen.
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">JID del grupo</label>
          <input
            type="text"
            placeholder="1203631234@g.us"
            {...register('group_jid')}
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-slate-400"
          />
          {errors.group_jid && (
            <p className="mt-1 text-xs text-rose-600">{errors.group_jid.message}</p>
          )}
          <p className="mt-1 text-xs text-slate-500">
            Buscalo en el gateway Open-WA con <code>GET /api/sessions/default/groups</code>.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <input
            id="wa-grp-active"
            type="checkbox"
            defaultChecked
            {...register('is_active')}
            className="rounded border-slate-300"
          />
          <label htmlFor="wa-grp-active" className="text-sm text-slate-700">
            Activar inmediatamente
          </label>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-slate-600 hover:text-slate-900 rounded-lg"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-3 py-1.5 bg-slate-900 text-white text-sm rounded-lg hover:bg-slate-800 transition disabled:opacity-50"
          >
            {isSubmitting ? <Loader2 size={14} className="animate-spin" /> : 'Registrar'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

interface DetectedGroup {
  group_jid: string;
  name: string;
  participants_count: number | null;
}

/**
 * Panel "Detectar grupos del bot": llama al gateway para listar TODOS los
 * grupos donde el bot es participante. Filtra los que ya están registrados
 * y permite agregar uno con un click. Si el gateway no responde (no
 * instalado / sesión no conectada) la sección queda colapsada con un aviso.
 */
function DetectGroupsPanel({
  registeredJids,
  onRegistered,
}: {
  registeredJids: string[];
  onRegistered: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [detected, setDetected] = useState<DetectedGroup[]>([]);
  const [registering, setRegistering] = useState<string | null>(null);

  const detect = async () => {
    setLoading(true);
    setError('');
    try {
      const data = await apiFetch<DetectedGroup[]>(`/admin/whatsapp/gateway/groups`);
      setDetected(data);
      setOpen(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudieron listar los grupos.');
    } finally {
      setLoading(false);
    }
  };

  const register = async (g: DetectedGroup) => {
    setRegistering(g.group_jid);
    try {
      await apiFetch('/admin/whatsapp/groups', {
        method: 'POST',
        body: { group_jid: g.group_jid, name: g.name, is_active: true },
      });
      notify.success('Grupo registrado.', g.name);
      onRegistered();
      // Sacamos el grupo de la lista in-place — no recargamos el detect.
      setDetected((curr) => curr.filter((d) => d.group_jid !== g.group_jid));
    } catch (err) {
      notify.apiError(err, 'No se pudo registrar el grupo.');
    } finally {
      setRegistering(null);
    }
  };

  const pending = detected.filter((g) => !registeredJids.includes(g.group_jid));

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-medium text-slate-900">Detectar grupos del bot</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            Pedimos al gateway la lista de grupos donde el bot es participante y registrás los que quieras con un click.
          </p>
        </div>
        <button
          onClick={detect}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-300 text-slate-700 text-sm rounded-lg hover:bg-slate-100 transition disabled:opacity-50"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
          Detectar
        </button>
      </div>

      {error && (
        <div className="mt-3 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
          {error}
        </div>
      )}

      {open && !error && (
        <div className="mt-3 space-y-1.5">
          {pending.length === 0 ? (
            <div className="text-xs text-slate-500 flex items-center gap-1.5">
              <CircleCheck size={14} className="text-emerald-600" />
              {detected.length === 0
                ? 'El bot no está en ningún grupo aún. Agregalo manualmente desde WhatsApp.'
                : 'Todos los grupos donde está el bot ya están registrados.'}
            </div>
          ) : (
            pending.map((g) => (
              <div
                key={g.group_jid}
                className="flex items-center justify-between gap-3 px-3 py-2 bg-white border border-slate-200 rounded-lg"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-slate-900 font-medium truncate">{g.name}</div>
                  <div className="text-xs text-slate-500 font-mono truncate">
                    {g.group_jid}
                    {g.participants_count !== null && ` · ${g.participants_count} miembros`}
                  </div>
                </div>
                <button
                  onClick={() => register(g)}
                  disabled={registering === g.group_jid}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs bg-slate-900 text-white rounded-md hover:bg-slate-800 transition disabled:opacity-50 shrink-0"
                >
                  {registering === g.group_jid ? (
                    <Loader2 size={12} className="animate-spin" />
                  ) : (
                    <Plus size={12} />
                  )}
                  Registrar
                </button>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
